import { useState } from "react";
import Reveal from "@/components/base/Reveal";
import Badge from "@/components/base/Badge";
import Eyebrow from "@/components/base/Eyebrow";
import DetailModal from "@/components/base/DetailModal";
import { skinProducts, type DetailOption } from "@/mocks/site";
import { buildWhatsAppLink } from "@/utils/links";

type Tone = "beige" | "clinic" | "rose" | "lilac";

const cardTones: Tone[] = ["beige", "clinic", "rose", "lilac"];

const toneStyles: Record<Tone, { halo: string; shadow: string; plate: string }> = {
  beige: { halo: "glow-beige", shadow: "hover:shadow-beige-soft", plate: "bg-background-200/60" },
  clinic: { halo: "glow-clinic", shadow: "hover:shadow-clinic-soft", plate: "bg-background-200/50" },
  rose: { halo: "glow-rose", shadow: "hover:shadow-rose-soft", plate: "bg-background-200/60" },
  lilac: { halo: "glow-lilac", shadow: "hover:shadow-lilac-soft", plate: "bg-background-200/50" },
};

export default function SkinSection() {
  const [showAll, setShowAll] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const visibleProducts = showAll ? skinProducts : skinProducts.slice(0, 8);
  const active = activeIndex === null ? null : skinProducts[activeIndex];

  return (
    <section id="productos" className="relative overflow-hidden bg-background-50">
      <div className="gradient-skin pointer-events-none absolute inset-0 opacity-80" />
      <div className="pointer-events-none absolute -left-24 top-1/3 h-[380px] w-[380px] rounded-full glow-beige blur-3xl" />
      <div className="pointer-events-none absolute -right-24 top-16 h-[420px] w-[420px] rounded-full glow-clinic blur-3xl" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px line-gold" />

      <div className="relative mx-auto w-full max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
        <Reveal className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-xl">
            <Eyebrow>Unidad 02 · ALÉA Skin</Eyebrow>
            <h2 className="mt-4 font-heading text-4xl font-bold leading-[1.05] text-foreground-950 md:text-5xl">
              Skincare profesional para casa
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-foreground-600">
              Nuestra línea propia de cuidado de la piel: fórmulas simples y bien pensadas para
              mantener tus resultados.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <a
              href={buildWhatsAppLink(
                "Hola ALÉA, quiero una recomendación de rutina para mi tipo de piel."
              )}
              target="_blank"
              rel="noreferrer"
              className="cta-primary inline-flex min-h-[48px] items-center justify-center gap-2 whitespace-nowrap rounded-[16px] bg-primary-500 px-6 font-label text-[11px] font-semibold uppercase tracking-[0.14em] text-background-50 transition-all duration-300 hover:bg-primary-600"
            >
              Recibir recomendación
              <i className="ri-magic-line text-base" />
            </a>
          </div>
        </Reveal>

        <div
          data-product-shop="alea-skin"
          className="mt-12 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4"
        >
          {visibleProducts.map((product, index) => {
            const tone = toneStyles[cardTones[index % cardTones.length]];
            return (
              <Reveal key={product.name} delay={(index % 4) * 70} className="relative flex">
                <span
                  aria-hidden
                  className={`pointer-events-none absolute -inset-3 rounded-[36px] opacity-50 blur-2xl ${tone.halo}`}
                />
                <article
                  className={`card-soft group relative flex w-full flex-col overflow-hidden rounded-[24px] border border-secondary-200 bg-background-50 transition-all duration-500 hover:-translate-y-1 ${tone.shadow}`}
                >
                  <button
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Ver detalle y comprar: ${product.name}`}
                    className="flex w-full flex-col text-left"
                  >
                    <div className="relative h-[200px] w-full overflow-hidden bg-background-100 sm:h-[260px]">
                      <span className={`pointer-events-none absolute inset-x-6 bottom-4 top-8 rounded-[24px] ${tone.plate}`} />
                      <img
                        src={product.image}
                        alt={`${product.name} — ALÉA Skin`}
                        title={`${product.name} ALÉA Skin cuidado de la piel`}
                        className="relative h-full w-full object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                      />
                      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-primary-950/20 to-transparent" />
                      <Badge tone="neutral" className="absolute left-3 top-3">
                        {product.tag}
                      </Badge>
                      {product.flag ? (
                        <Badge tone="gold" className="absolute right-3 top-3">
                          {product.flag}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="px-4 pt-4 sm:px-5 sm:pt-5">
                      <span className="font-label text-[10px] font-medium uppercase tracking-[0.18em] text-foreground-400">
                        {product.category}
                      </span>
                      <h3 className="mt-1.5 font-heading text-base font-bold leading-snug text-foreground-950 sm:text-lg">
                        {product.name}
                      </h3>
                      <p className="mt-1 hidden text-[13px] leading-relaxed text-foreground-500 sm:block">
                        {product.benefit}
                      </p>
                    </div>
                  </button>

                  <div className="mt-auto flex items-center justify-between gap-2 px-4 pb-4 pt-4 sm:px-5 sm:pb-5">
                    <span className="font-heading text-xl font-extrabold text-foreground-950 sm:text-2xl">
                      {product.price}
                    </span>
                    <a
                      href={buildWhatsAppLink(`Hola ALÉA, quiero comprar ${product.name}.`)}
                      target="_blank"
                      rel="noreferrer"
                      className="cta-primary inline-flex min-h-[40px] items-center justify-center gap-1.5 whitespace-nowrap rounded-[12px] bg-primary-500 px-4 font-label text-[10px] font-semibold uppercase tracking-[0.12em] text-background-50 transition-all duration-300 hover:bg-primary-600"
                    >
                      <i className="ri-shopping-bag-3-line text-sm" />
                      Comprar
                    </a>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-12 flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="cta-outline inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-full border border-secondary-300 bg-background-50 px-7 font-label text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-950 transition-all duration-300 hover:border-accent-400"
          >
            {showAll ? "Ver menos productos" : `Ver catálogo completo (${skinProducts.length})`}
            <i className={showAll ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
          </button>
        </div>
      </div>

      <DetailModal
        open={active !== null}
        onClose={() => setActiveIndex(null)}
        unitLabel={`Unidad 02 · ALÉA Skin · ${active?.category ?? ""}`}
        title={active?.name ?? ""}
        image={active?.image ?? ""}
        price={active?.price}
        description={active?.description ?? ""}
        optionsTitle={active?.optionsTitle}
        options={active?.options}
        includesTitle="Indicación de uso"
        includes={active ? [active.indication] : []}
        ctaLabel="Comprar por WhatsApp"
        buildCtaHref={(option?: DetailOption) =>
          buildWhatsAppLink(
            option
              ? `Hola ALÉA, quiero comprar ${active?.name} — ${option.label} (${option.price}).`
              : `Hola ALÉA, quiero comprar ${active?.name}.`
          )
        }
      />
    </section>
  );
}